import { forwardRef, useMemo } from 'react';
import {
  Modal,
  Pressable,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { withUnistyles } from 'react-native-unistyles';

import { useTooltipContext } from './TooltipContext';
import { styles } from './styles';
import type { TooltipContentProps } from './types';

const ThemedView = withUnistyles(View);
const TOOLTIP_MAX_WIDTH = 250;
const TOOLTIP_ESTIMATED_HEIGHT = 50;
const SCREEN_PADDING = 16;

const TooltipContent = forwardRef<View, TooltipContentProps>(
  ({ children, sideOffset = 8, modalProps, ...props }, ref) => {
    const { isVisible, hideTooltip, triggerLayout, placement } =
      useTooltipContext();

    const screenDimensions = useWindowDimensions();

    const tooltipPosition = useMemo(() => {
      if (!triggerLayout) {
        return { top: 0, left: 0 };
      }

      const { x, y, width, height } = triggerLayout;
      let top = 0;
      let left = 0;

      switch (placement) {
        case 'top':
          top = y - sideOffset;
          left = x + width / 2;
          break;
        case 'bottom':
          top = y + height + sideOffset;
          left = x + width / 2;
          break;
        case 'left':
          top = y + height / 2;
          left = x - sideOffset;
          break;
        case 'right':
          top = y + height / 2;
          left = x + width + sideOffset;
          break;
      }

      if (left + TOOLTIP_MAX_WIDTH > screenDimensions.width) {
        left = screenDimensions.width - TOOLTIP_MAX_WIDTH - SCREEN_PADDING;
      }
      if (left < SCREEN_PADDING) {
        left = SCREEN_PADDING;
      }
      if (top + TOOLTIP_ESTIMATED_HEIGHT > screenDimensions.height) {
        top =
          screenDimensions.height - TOOLTIP_ESTIMATED_HEIGHT - SCREEN_PADDING;
      }
      if (top < SCREEN_PADDING) {
        top = SCREEN_PADDING;
      }

      return { top, left };
    }, [triggerLayout, placement, sideOffset, screenDimensions]);

    if (!isVisible || !triggerLayout) {
      return null;
    }

    return (
      <Modal
        transparent
        visible={isVisible}
        animationType="fade"
        onRequestClose={hideTooltip}
        {...modalProps}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={hideTooltip}
          testID="tooltip-overlay"
        >
          <View
            style={[
              styles.modalContainer,
              {
                top: tooltipPosition.top,
                left: tooltipPosition.left,
              },
            ]}
          >
            <ThemedView ref={ref} style={[styles.tooltipContent]} {...props}>
              {typeof children === 'string' ? (
                <Text style={styles.tooltipText}>{children}</Text>
              ) : (
                children
              )}
            </ThemedView>
          </View>
        </Pressable>
      </Modal>
    );
  },
);

TooltipContent.displayName = 'TooltipContent';

export { TooltipContent };
